import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Bookmark } from 'lucide-react';

import OptimizedImage from '@/components/media/OptimizedImage';
import { pathFor } from '@/data/routes';
import { photoForArticle } from '@/data/photos';
import { useBookmarks } from '@/hooks/useBookmarks';
import { getCategoryBySlug } from '@/data/categories';
// 2026-05-16 — Phase 1 perf : prefetch du chunk Article.jsx au survol
// d'une ArticleCard. Le browser cache le chunk, React le pick au clic →
// 0 ms perçu pour ouvrir l'article (vs ~200-400 ms en cold-cache 4G).
import { usePrefetchOnHover } from '@/hooks/usePrefetchOnHover';

const COVER_BY_CATEGORY = {
  'visa-residence': '/covers/visa-residence.svg',
  'banque-argent': '/covers/banque-argent.svg',
  'assurance': '/covers/assurance.svg',
  'animaux-chiens': '/covers/animaux-chiens.svg',
  'vols': '/covers/vols.svg',
  'espagnol': '/covers/espagnol.svg',
  'vie-quotidienne': '/covers/vie-quotidienne.svg',
  'immobilier': '/covers/immobilier.svg',
};
function coverFor(category) {
  return COVER_BY_CATEGORY[category] ?? '/og-default.svg';
}

/**
 * Editorial article card — image (16:9) + category eyebrow + Lora italic title
 * + excerpt + meta (author, date, reading time). Pillar articles get a star.
 *
 * Migration mai 2026 : teal → bordeaux pour eyebrow, hover title, star pillar.
 */
export default function ArticleCard({ article, lang = 'fr', className = '', priority = false }) {
  const { t, i18n } = useTranslation();
  const reduce = useReducedMotion();
  const { isBookmarked, toggle: toggleBookmark } = useBookmarks();

  if (!article) return null;

  const {
    slug, title, excerpt, image, imageWebp, category, categoryLabel,
    publishedAt, readTime, pillar = false, author,
  } = article;

  const href = pathFor('article', lang, { category, slug });
  const saved = isBookmarked(slug, lang);
  // Prefetch le chunk Article.jsx au survol/focus du Link. Le hook
  // expose { onPointerEnter, onFocus } à spread sur le <Link>. Idempotent
  // (Vite cache le chunk après le 1er fetch, les hovers suivants sont
  // no-ops).
  const prefetch = usePrefetchOnHover(() => import('@/pages/Article'));

  const handleBookmark = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleBookmark({ slug, lang, category, title });
  };

  const dateLabel = publishedAt
    ? new Date(publishedAt).toLocaleDateString(i18n.language || lang, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

  const hoverProps = reduce
    ? {}
    : {
        whileHover: { y: -3 },
        whileFocus: { y: -3 },
        transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
      };

  return (
    <motion.article
      {...hoverProps}
      className={`card card-hoverable group relative ${className}`}
    >
      {/* 2026-05-26 — Audit design Sprint 2 (I·02) :
          Bookmark plus en overlay permanent. Comportement :
          - Saved → toujours visible (l'utilisateur a marqué l'article,
            on lui montre que l'état est persisté)
          - Non saved → invisible par défaut, révélé au hover/focus de la
            card sur desktop ; sur mobile (touch), invisible pour ne pas
            polluer les couvertures (le geste tap-and-hold ou la page
            article elle-même permettent l'ajout).
          Le bouton garde sa réactivité (toggle d'1 clic). */}
      <button
        type="button"
        onClick={handleBookmark}
        aria-pressed={saved}
        aria-label={
          saved
            ? t('common:bookmarkRemove', { defaultValue: 'Retirer des favoris' })
            : t('common:bookmarkAdd', { defaultValue: 'Sauvegarder dans mes favoris' })
        }
        className={`absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full shadow-card backdrop-blur-sm transition-all hover:scale-110 focus-visible:opacity-100 motion-safe:transition-opacity ${
          saved
            ? 'bg-bordeaux-600 text-cream-50'
            : 'bg-cream-50/85 text-navy-900 opacity-0 hover:bg-cream-50 group-hover:opacity-100 group-focus-within:opacity-100 max-md:hidden dark:bg-black/70 dark:text-white dark:hover:bg-black/85'
        }`}
      >
        <Bookmark
          size={15}
          aria-hidden="true"
          className={saved ? 'fill-current' : ''}
        />
      </button>

      <Link to={href} className="block focus-visible:outline-none" {...prefetch}>
        <div className="aspect-video overflow-hidden bg-cream-100 dark:bg-white/5">
          <OptimizedImage
            src={
              photoForArticle({ slug, category, featuredImage: image })?.url ??
              coverFor(category)
            }
            webpSrc={imageWebp}
            alt=""
            width={800}
            height={450}
            priority={priority}
            // Route external images (Wikimedia / Unsplash / Pexels) through
            // Netlify Image CDN. The CDN auto-negotiates AVIF/WebP based on
            // Accept headers — fixes the "image doesn't show until I click"
            // symptom (the bare WebP guess at upload.wikimedia.org returns
            // 404 since Wikimedia only serves the original .jpg, so the
            // <picture> element flickered before falling back).
            cdnProxy
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>

        <div className="p-5">
          {(categoryLabel || category) && (
            <p className="v4-eyebrow mb-2.5 text-bordeaux-700 dark:text-bordeaux-400">
              {/* 2026-05-17 — Resolve categoryLabel from the category slug +
                  active lang when the parent didn't pass it pre-resolved.
                  Avoids leaking FR slugs (e.g. "ESPAGNOL", "BANQUE-ARGENT")
                  on /en/ and /es/ Home cards. Falls back to the raw slug
                  only if the category isn't in the registry at all. */}
              {categoryLabel
                ?? getCategoryBySlug(category)?.label?.[lang]
                ?? getCategoryBySlug(category)?.label?.fr
                ?? category}
            </p>
          )}

          <h3 className="line-clamp-2 font-serif text-xl font-bold italic leading-snug text-navy-900 transition-colors group-hover:text-bordeaux-700 dark:text-white dark:group-hover:text-bordeaux-400">
            {pillar && (
              <span className="mr-1.5 inline-flex translate-y-[-1px] align-middle text-gold-500" aria-label={t('common:premium')}>
                <StarIcon />
              </span>
            )}
            {title}
          </h3>

          {excerpt && (
            <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-700 dark:text-white/70">{excerpt}</p>
          )}

          <div className="mt-4 flex items-center gap-2.5 text-xs text-gray-700 dark:text-white/60">
            {author?.photo && (
              <img
                src={author.photo}
                alt=""
                width={24}
                height={24}
                loading="lazy"
                decoding="async"
                className="h-6 w-6 rounded-full bg-cream-100 object-cover dark:bg-white/10"
              />
            )}
            {author?.name && (
              <>
                <span className="font-medium text-gray-700 dark:text-white/70">{author.name}</span>
                <span aria-hidden="true">·</span>
              </>
            )}
            {dateLabel && <time dateTime={publishedAt}>{dateLabel}</time>}
            {readTime && (
              <>
                <span aria-hidden="true">·</span>
                <span>{t('common:readingTime', { minutes: readTime })}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 32 32" width={14} height={14} fill="currentColor" aria-hidden="true">
      <path d="M16 2 L18.6 13.4 L30 16 L18.6 18.6 L16 30 L13.4 18.6 L2 16 L13.4 13.4 Z" />
    </svg>
  );
}
